import React, { useState, useEffect, useRef } from 'react';
import {
  X, Save, Trash2, Copy, AlertCircle, CheckCircle,
  RefreshCw, Key, Clock, Shield, Eye, Edit, Search,
  RotateCcw, XCircle, AlertTriangle
} from 'lucide-react';
import { ApiKey, ApiKeyCreateResponse, apiService } from '../services/api';
import {
  clearPendingIdempotencyAttempt,
  findPendingIdempotencyAttempt,
  getOrCreatePendingIdempotencyAttempt,
  ONE_TIME_CREDENTIAL_ATTEMPT_TTL_MS,
  PendingIdempotencyAttempt,
} from '../services/pendingIdempotencyAttempt';
import {
  buildAsyncRequestOwnerKey,
  isCurrentAsyncRequest,
} from '../utils/asyncRequestOwnership';
import { useTabActivity } from '../contexts/TabActivityContext';
import './ApiKeyDetailSidebar.css';

interface ApiKeyDetailSidebarProps {
  apiKey: ApiKey | null;
  isOpen: boolean;
  onClose: () => void;
  onSave?: (apiKey: ApiKey) => void;
  onDelete?: (keyId: string) => void;
  isCreating?: boolean;
}

// Available scopes with descriptions
const AVAILABLE_SCOPES = [
  { value: 'search', label: 'Search', description: 'Search indexes and filter results', icon: Search },
  { value: 'read', label: 'Read', description: 'Read data (includes search)', icon: Eye },
  { value: 'write', label: 'Write', description: 'Create and modify data (includes read)', icon: Edit },
  {
    value: 'admin',
    label: 'Admin',
    description: 'Full access within your account; never platform administrator',
    icon: Shield,
  },
];

const API_KEY_PENDING_ATTEMPT_NAMESPACE = 'api-key-management';

interface CompletedApiKeyCredentialAttempt {
  operation: 'create' | 'rotate';
  attempt: PendingIdempotencyAttempt;
  responseOwnerId: string;
}

const ApiKeyDetailSidebar: React.FC<ApiKeyDetailSidebarProps> = ({
  apiKey,
  isOpen,
  onClose,
  onSave,
  onDelete,
  isCreating = false
}) => {
  const [sidebarWidth, setSidebarWidth] = useState<number>(() => {
    const saved = localStorage.getItem('apiKeySidebarWidth');
    return saved ? parseInt(saved, 10) : 600;
  });
  const { isActive } = useTabActivity();
  const [isResizing, setIsResizing] = useState(false);
  const [isEditing, setIsEditing] = useState(isCreating);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [isRotating, setIsRotating] = useState(false);
  const [hasPendingRotationRecovery, setHasPendingRotationRecovery] = useState(false);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showRevokeConfirm, setShowRevokeConfirm] = useState(false);
  const [showRotateConfirm, setShowRotateConfirm] = useState(false);

  // Form fields
  const [keyName, setKeyName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['read']);
  const [expiresInDays, setExpiresInDays] = useState<number | null>(null);

  // New key display (only shown once on creation or rotation)
  const [newKeyValue, setNewKeyValue] = useState<string | null>(null);
  const [keyCopied, setKeyCopied] = useState(false);

  const modalRef = useRef<HTMLDivElement>(null);
  const resizeStartX = useRef<number>(0);
  const resizeStartWidth = useRef<number>(0);
  const displayedKeyOwnerIdRef = useRef<string | null>(null);
  const createAttemptRef = useRef<PendingIdempotencyAttempt | null>(null);
  const rotateAttemptRef = useRef<PendingIdempotencyAttempt | null>(null);
  const createRequestInFlightRef = useRef(false);
  const rotateRequestInFlightRef = useRef(false);
  const isMountedRef = useRef(false);
  const credentialRequestGenerationRef = useRef(0);
  const completedCredentialAttemptRef = useRef<
  CompletedApiKeyCredentialAttempt | null
  >(null);
  const credentialOwnerKey = buildAsyncRequestOwnerKey(
    isOpen ? 'open' : 'closed',
    isCreating ? 'create' : 'manage',
    isCreating ? null : apiKey?.key_id,
  );
  const credentialOwnerKeyRef = useRef(credentialOwnerKey);
  if (credentialOwnerKeyRef.current !== credentialOwnerKey) {
    credentialOwnerKeyRef.current = credentialOwnerKey;
    credentialRequestGenerationRef.current += 1;
  }

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      credentialRequestGenerationRef.current += 1;
    };
  }, []);

  useEffect(() => {
    const completed = completedCredentialAttemptRef.current;
    if (
      !isOpen
      || !newKeyValue
      || !completed
      || displayedKeyOwnerIdRef.current !== completed.responseOwnerId
    ) {
      return;
    }
    clearPendingIdempotencyAttempt({
      namespace: API_KEY_PENDING_ATTEMPT_NAMESPACE,
      operation: completed.operation,
      requestFingerprint: completed.attempt.requestFingerprint,
      expectedIdempotencyKey: completed.attempt.idempotencyKey,
    });
    if (
      completed.operation === 'create'
      && createAttemptRef.current?.idempotencyKey
        === completed.attempt.idempotencyKey
    ) {
      createAttemptRef.current = null;
    }
    if (
      completed.operation === 'rotate'
      && rotateAttemptRef.current?.idempotencyKey
        === completed.attempt.idempotencyKey
    ) {
      rotateAttemptRef.current = null;
      setHasPendingRotationRecovery(false);
    }
    completedCredentialAttemptRef.current = null;
  }, [apiKey?.key_id, isCreating, isOpen, newKeyValue]);

  // Initialize form from API key
  useEffect(() => {
    if (apiKey && !isCreating) {
      setKeyName(apiKey.name);
      setSelectedScopes(apiKey.scopes || ['read']);
      setIsEditing(false);
      if (displayedKeyOwnerIdRef.current !== apiKey.key_id) {
        displayedKeyOwnerIdRef.current = null;
        setNewKeyValue(null);
        setKeyCopied(false);
      }
    } else if (isCreating) {
      setKeyName('');
      setSelectedScopes(['read']);
      setExpiresInDays(null);
      setIsEditing(true);
      displayedKeyOwnerIdRef.current = null;
      setNewKeyValue(null);
      setKeyCopied(false);
    }
  }, [apiKey, isCreating]);

  useEffect(() => {
    let cancelled = false;
    setHasPendingRotationRecovery(false);
    if (!apiKey || isCreating || !apiKey.revoked_at) {
      return () => {
        cancelled = true;
      };
    }

    void findPendingIdempotencyAttempt({
      namespace: API_KEY_PENDING_ATTEMPT_NAMESPACE,
      operation: 'rotate',
      requestPayload: { key_id: apiKey.key_id },
      inMemoryAttempt: rotateAttemptRef.current || undefined,
    }).then((attempt) => {
      if (cancelled || !attempt) return;
      rotateAttemptRef.current = attempt;
      setHasPendingRotationRecovery(true);
    }).catch(() => {
      // A read-only recovery probe must never dispatch a replacement rotation.
    });

    return () => {
      cancelled = true;
    };
  }, [apiKey, isCreating]);

  // Handle ESC key
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !isEditing && !showDeleteConfirm && !showRevokeConfirm && !showRotateConfirm) {
        onClose();
      }
    };

    if (isOpen && isActive) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isActive, isOpen, isEditing, onClose, showDeleteConfirm, showRevokeConfirm, showRotateConfirm]);

  // Save width to localStorage
  useEffect(() => {
    localStorage.setItem('apiKeySidebarWidth', sidebarWidth.toString());
    if (isOpen) {
      window.dispatchEvent(new CustomEvent('apiKeySidebarWidthChange', { detail: { width: sidebarWidth } }));
    }
  }, [sidebarWidth, isOpen]);

  // Resize handlers
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsResizing(true);
    resizeStartX.current = e.clientX;
    resizeStartWidth.current = sidebarWidth;
  };

  useEffect(() => {
    if (!isActive || !isOpen) {
      setIsResizing(false);
      return;
    }
    if (!isResizing) return;
    document.body.classList.add('resizing-apikey-sidebar');

    const handleMouseMove = (e: MouseEvent) => {
      const deltaX = resizeStartX.current - e.clientX;
      const newWidth = Math.max(400, Math.min(900, resizeStartWidth.current + deltaX));
      setSidebarWidth(newWidth);
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.classList.remove('resizing-apikey-sidebar');
    };
  }, [isActive, isOpen, isResizing]);

  // Toggle scope selection
  const toggleScope = (scope: string) => {
    setSelectedScopes(prev =>
      prev.includes(scope)
        ? prev.filter(s => s !== scope)
        : [...prev, scope]
    );
  };

  // Validate form
  const validateForm = (): string | null => {
    if (!keyName.trim()) return 'API key name is required';
    if (keyName.trim().length < 1 || keyName.trim().length > 100) {
      return 'API key name must be between 1 and 100 characters';
    }
    if (selectedScopes.length === 0) return 'At least one scope must be selected';
    if (expiresInDays !== null && (expiresInDays < 1 || expiresInDays > 365)) {
      return 'Expiration must be between 1 and 365 days';
    }
    return null;
  };

  // Save API key
  const handleSave = async () => {
    if (createRequestInFlightRef.current) return;

    const validationError = validateForm();
    if (validationError) {
      setMessage({ type: 'error', text: validationError });
      return;
    }

    createRequestInFlightRef.current = true;
    setIsSaving(true);
    setMessage(null);
    const requestToken = {
      generation: credentialRequestGenerationRef.current + 1,
      ownerKey: credentialOwnerKeyRef.current,
    };
    credentialRequestGenerationRef.current = requestToken.generation;
    const ownsRequest = () => (
      isMountedRef.current
      && isCurrentAsyncRequest(
        credentialRequestGenerationRef.current,
        credentialOwnerKeyRef.current,
        requestToken,
      )
    );

    try {
      if (isCreating) {
        const request = {
          name: keyName.trim(),
          scopes: [...selectedScopes].sort(),
          expires_in_days: expiresInDays || undefined,
        };
        const attempt = await getOrCreatePendingIdempotencyAttempt({
          namespace: API_KEY_PENDING_ATTEMPT_NAMESPACE,
          operation: 'create',
          requestPayload: request,
          keyPrefix: 'api-key-create',
          inMemoryAttempt: createAttemptRef.current || undefined,
          ttlMs: ONE_TIME_CREDENTIAL_ATTEMPT_TTL_MS,
        });
        createAttemptRef.current = attempt;
        if (!ownsRequest()) {
          return;
        }

        // Create new API key
        const response: ApiKeyCreateResponse = await apiService.createApiKey(
          request,
          attempt.idempotencyKey,
        );
        if (!ownsRequest()) {
          return;
        }

        // Show the new key (only available once)
        completedCredentialAttemptRef.current = {
          operation: 'create',
          attempt,
          responseOwnerId: response.key_id,
        };
        displayedKeyOwnerIdRef.current = response.key_id;
        setNewKeyValue(response.key);
        setIsEditing(false);

        // Notify parent
        if (onSave) {
          onSave(response);
        }

        setMessage({ type: 'success', text: 'API key created successfully. Copy it now - it will not be shown again!' });
      }
    } catch (error: any) {
      if (!ownsRequest()) {
        return;
      }
      console.error('Failed to save API key:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || error.message || 'Failed to save API key'
      });
    } finally {
      createRequestInFlightRef.current = false;
      if (isMountedRef.current) {
        setIsSaving(false);
      }
    }
  };

  // Delete API key
  const handleDelete = async () => {
    if (!apiKey) return;

    setIsDeleting(true);
    setMessage(null);

    try {
      await apiService.deleteApiKey(apiKey.key_id);
      setMessage({ type: 'success', text: 'API key deleted successfully' });

      if (onDelete) {
        onDelete(apiKey.key_id);
      }

      setTimeout(() => {
        onClose();
      }, 500);
    } catch (error: any) {
      console.error('Failed to delete API key:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to delete API key'
      });
    } finally {
      setIsDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  // Revoke API key
  const handleRevoke = async () => {
    if (!apiKey) return;

    setIsRevoking(true);
    setMessage(null);

    try {
      const revokedKey = await apiService.revokeApiKey(apiKey.key_id);
      setMessage({ type: 'success', text: 'API key revoked successfully' });

      if (onSave) {
        onSave(revokedKey);
      }
    } catch (error: any) {
      console.error('Failed to revoke API key:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || 'Failed to revoke API key'
      });
    } finally {
      setIsRevoking(false);
      setShowRevokeConfirm(false);
    }
  };

  // Rotate API key
  const handleRotate = async () => {
    if (!apiKey || rotateRequestInFlightRef.current) return;

    const predecessorKeyId = apiKey.key_id;
    rotateRequestInFlightRef.current = true;
    setIsRotating(true);
    setMessage(null);
    const requestToken = {
      generation: credentialRequestGenerationRef.current + 1,
      ownerKey: credentialOwnerKeyRef.current,
    };
    credentialRequestGenerationRef.current = requestToken.generation;
    const ownsRequest = () => (
      isMountedRef.current
      && isCurrentAsyncRequest(
        credentialRequestGenerationRef.current,
        credentialOwnerKeyRef.current,
        requestToken,
      )
    );

    try {
      const request = { key_id: predecessorKeyId };
      const attempt = await getOrCreatePendingIdempotencyAttempt({
        namespace: API_KEY_PENDING_ATTEMPT_NAMESPACE,
        operation: 'rotate',
        requestPayload: request,
        keyPrefix: `api-key-rotate:${predecessorKeyId}`,
        inMemoryAttempt: rotateAttemptRef.current || undefined,
        ttlMs: ONE_TIME_CREDENTIAL_ATTEMPT_TTL_MS,
      });
      rotateAttemptRef.current = attempt;
      if (!ownsRequest()) {
        return;
      }
      const response = await apiService.rotateApiKey(
        predecessorKeyId,
        attempt.idempotencyKey,
      );
      if (!ownsRequest()) {
        return;
      }

      // Show the new key
      completedCredentialAttemptRef.current = {
        operation: 'rotate',
        attempt,
        responseOwnerId: response.key_id,
      };
      displayedKeyOwnerIdRef.current = response.key_id;
      setNewKeyValue(response.key);

      if (onSave) {
        onSave(response);
      }

      setMessage({ type: 'success', text: 'API key rotated successfully. Copy the new key now - it will not be shown again!' });
    } catch (error: any) {
      if (!ownsRequest()) {
        return;
      }
      console.error('Failed to rotate API key:', error);
      setMessage({
        type: 'error',
        text: error.response?.data?.detail || error.message || 'Failed to rotate API key'
      });
    } finally {
      rotateRequestInFlightRef.current = false;
      if (isMountedRef.current) {
        setIsRotating(false);
        setShowRotateConfirm(false);
      }
    }
  };

  // Copy key to clipboard
  const handleCopyKey = async () => {
    if (!newKeyValue) return;

    try {
      await navigator.clipboard.writeText(newKeyValue);
      setKeyCopied(true);
      setTimeout(() => setKeyCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy key:', error);
      setMessage({ type: 'error', text: 'Failed to copy key to clipboard' });
    }
  };

  // Copy key prefix to clipboard
  const handleCopyPrefix = async () => {
    if (!apiKey?.key_prefix) return;

    try {
      await navigator.clipboard.writeText(apiKey.key_prefix);
      setMessage({ type: 'success', text: 'Key prefix copied to clipboard' });
      setTimeout(() => setMessage(null), 2000);
    } catch (error) {
      console.error('Failed to copy prefix:', error);
    }
  };

  // Format date
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status info
  const getStatusInfo = () => {
    if (!apiKey) return { status: 'unknown', color: 'var(--text-muted)' };
    if (apiKey.revoked_at) return { status: 'Revoked', color: '#ef4444' };
    if (apiKey.expires_at && new Date(apiKey.expires_at) < new Date()) return { status: 'Expired', color: '#f59e0b' };
    if (apiKey.is_active) return { status: 'Active', color: '#22c55e' };
    return { status: 'Inactive', color: 'var(--text-muted)' };
  };

  if (!isOpen) return null;

  const statusInfo = getStatusInfo();

  return (
    <div
      ref={modalRef}
      className={`apikey-detail-sidebar ${isOpen ? 'open' : ''}`}
      style={{ width: sidebarWidth }}
    >
      {/* Resize handle */}
      <div
        className="sidebar-resize-handle"
        onMouseDown={handleResizeStart}
      />

      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-title">
          <Key size={20} />
          <h2>{isCreating ? 'Create API Key' : 'API Key Details'}</h2>
        </div>
        <button className="close-btn" onClick={onClose}>
          <X size={20} />
        </button>
      </div>

      {/* Content */}
      <div className="sidebar-content">
        {/* New Key Display (only after creation or rotation) */}
        {newKeyValue && (
          <div className="new-key-section">
            <div className="new-key-warning">
              <AlertTriangle size={20} />
              <div>
                <strong>Important!</strong>
                <p>Copy your API key now. You will not be able to see it again.</p>
              </div>
            </div>
            <div className="new-key-value">
              <code>{newKeyValue}</code>
              <button
                className={`copy-key-btn ${keyCopied ? 'copied' : ''}`}
                onClick={handleCopyKey}
                title="Copy to clipboard"
              >
                {keyCopied ? <CheckCircle size={16} /> : <Copy size={16} />}
                {keyCopied ? 'Copied!' : 'Copy'}
              </button>
            </div>
          </div>
        )}

        {/* Message */}
        {message && (
          <div className={`sidebar-message ${message.type}`}>
            {message.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
            <span>{message.text}</span>
            <button className="message-close" onClick={() => setMessage(null)}>
              <X size={14} />
            </button>
          </div>
        )}

        {/* Status Badge (not for creating) */}
        {!isCreating && apiKey && (
          <div className="status-section">
            <div className="status-badge-large" style={{ backgroundColor: `${statusInfo.color}15`, color: statusInfo.color }}>
              {apiKey.revoked_at ? <XCircle size={18} /> :
               apiKey.is_active ? <CheckCircle size={18} /> : <AlertTriangle size={18} />}
              <span>{statusInfo.status}</span>
            </div>
          </div>
        )}

        {/* Key Prefix (not for creating) */}
        {!isCreating && apiKey && (
          <div className="detail-section">
            <label>Key Identifier</label>
            <div className="key-prefix-display">
              <code>{apiKey.key_prefix}...</code>
              <button
                className="copy-prefix-btn"
                onClick={handleCopyPrefix}
                title="Copy key prefix"
              >
                <Copy size={14} />
              </button>
            </div>
          </div>
        )}

        {/* Name Field */}
        <div className="detail-section">
          <label>Name {isEditing && <span className="required">*</span>}</label>
          {isEditing ? (
            <input
              type="text"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder="Enter a name for this API key"
              className="form-input"
              maxLength={100}
            />
          ) : (
            <div className="detail-value">{apiKey?.name}</div>
          )}
        </div>

        {/* Scopes Field */}
        <div className="detail-section">
          <label>Scopes {isEditing && <span className="required">*</span>}</label>
          <div className="scopes-description">
            Higher scopes include all permissions from lower scopes.
          </div>
          <div className="scopes-grid">
            {AVAILABLE_SCOPES.map(scope => {
              const Icon = scope.icon;
              const isSelected = selectedScopes.includes(scope.value);
              const isDisabled = !isEditing && !isCreating;

              return (
                <div
                  key={scope.value}
                  className={`scope-option ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
                  onClick={() => !isDisabled && toggleScope(scope.value)}
                >
                  <div className="scope-option-header">
                    <div className={`scope-checkbox ${isSelected ? 'checked' : ''}`}>
                      {isSelected && <CheckCircle size={14} />}
                    </div>
                    <Icon size={16} />
                    <span className="scope-label">{scope.label}</span>
                  </div>
                  <div className="scope-description">{scope.description}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Expiration Field (only for creating) */}
        {isCreating && (
          <div className="detail-section">
            <label>Expiration (optional)</label>
            <div className="expiration-options">
              <button
                className={`expiration-btn ${expiresInDays === null ? 'selected' : ''}`}
                onClick={() => setExpiresInDays(null)}
              >
                Never
              </button>
              <button
                className={`expiration-btn ${expiresInDays === 30 ? 'selected' : ''}`}
                onClick={() => setExpiresInDays(30)}
              >
                30 days
              </button>
              <button
                className={`expiration-btn ${expiresInDays === 90 ? 'selected' : ''}`}
                onClick={() => setExpiresInDays(90)}
              >
                90 days
              </button>
              <button
                className={`expiration-btn ${expiresInDays === 365 ? 'selected' : ''}`}
                onClick={() => setExpiresInDays(365)}
              >
                1 year
              </button>
            </div>
            <div className="custom-expiration">
              <span>Or custom:</span>
              <input
                type="number"
                min="1"
                max="365"
                value={expiresInDays || ''}
                onChange={(e) => setExpiresInDays(e.target.value ? parseInt(e.target.value) : null)}
                placeholder="Days"
                className="days-input"
              />
              <span>days</span>
            </div>
          </div>
        )}

        {/* Metadata (not for creating) */}
        {!isCreating && apiKey && (
          <div className="detail-section">
            <label>Details</label>
            <div className="metadata-grid">
              <div className="metadata-item">
                <Clock size={14} />
                <span className="metadata-label">Created</span>
                <span className="metadata-value">{formatDate(apiKey.created_at)}</span>
              </div>
              <div className="metadata-item">
                <RefreshCw size={14} />
                <span className="metadata-label">Last Used</span>
                <span className="metadata-value">{formatDate(apiKey.last_used_at)}</span>
              </div>
              {apiKey.expires_at && (
                <div className="metadata-item">
                  <AlertTriangle size={14} />
                  <span className="metadata-label">Expires</span>
                  <span className="metadata-value">{formatDate(apiKey.expires_at)}</span>
                </div>
              )}
              {apiKey.revoked_at && (
                <div className="metadata-item">
                  <XCircle size={14} />
                  <span className="metadata-label">Revoked</span>
                  <span className="metadata-value">{formatDate(apiKey.revoked_at)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="sidebar-footer">
        {isCreating ? (
          // Creating mode
          <>
            <button className="cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="save-btn"
              onClick={handleSave}
              disabled={isSaving || !!newKeyValue}
            >
              {isSaving ? <RefreshCw size={16} className="spinning" /> : <Save size={16} />}
              {isSaving ? 'Creating...' : 'Create API Key'}
            </button>
          </>
        ) : (
          // Viewing mode
          <>
            {/* Revoke button */}
            {apiKey && !apiKey.revoked_at && (
              <button
                className="revoke-btn"
                onClick={() => setShowRevokeConfirm(true)}
                disabled={isRevoking}
              >
                <XCircle size={16} />
                Revoke
              </button>
            )}

            {/* Rotate button */}
            {apiKey && (!apiKey.revoked_at || hasPendingRotationRecovery) && (
              <button
                className="rotate-btn"
                onClick={() => setShowRotateConfirm(true)}
                disabled={isRotating}
              >
                <RotateCcw size={16} />
                {hasPendingRotationRecovery ? 'Recover Rotated Key' : 'Rotate'}
              </button>
            )}

            {/* Delete button */}
            <button
              className="delete-btn"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isDeleting}
            >
              <Trash2 size={16} />
              Delete
            </button>
          </>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="confirm-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <AlertTriangle size={24} className="warning-icon" />
              <h3>Delete API Key?</h3>
            </div>
            <p>This action cannot be undone. The API key will be permanently deleted and can no longer be used for authentication.</p>
            <div className="confirm-modal-actions">
              <button className="cancel-btn" onClick={() => setShowDeleteConfirm(false)}>
                Cancel
              </button>
              <button className="confirm-delete-btn" onClick={handleDelete} disabled={isDeleting}>
                {isDeleting ? <RefreshCw size={16} className="spinning" /> : <Trash2 size={16} />}
                {isDeleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Confirmation Modal */}
      {showRevokeConfirm && (
        <div className="confirm-modal-overlay" onClick={() => setShowRevokeConfirm(false)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <AlertTriangle size={24} className="warning-icon" />
              <h3>Revoke API Key?</h3>
            </div>
            <p>This action cannot be undone. The API key will be immediately revoked and can no longer be used for authentication.</p>
            <div className="confirm-modal-actions">
              <button className="cancel-btn" onClick={() => setShowRevokeConfirm(false)}>
                Cancel
              </button>
              <button className="confirm-revoke-btn" onClick={handleRevoke} disabled={isRevoking}>
                {isRevoking ? <RefreshCw size={16} className="spinning" /> : <XCircle size={16} />}
                {isRevoking ? 'Revoking...' : 'Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rotate Confirmation Modal */}
      {showRotateConfirm && (
        <div className="confirm-modal-overlay" onClick={() => setShowRotateConfirm(false)}>
          <div className="confirm-modal" onClick={e => e.stopPropagation()}>
            <div className="confirm-modal-header">
              <RotateCcw size={24} className="warning-icon" />
              <h3>{hasPendingRotationRecovery ? 'Recover Rotated Key?' : 'Rotate API Key?'}</h3>
            </div>
            <p>
              {hasPendingRotationRecovery
                ? 'A previous rotation may have completed without returning its one-time key. Recover the exact prior response now.'
                : 'This will revoke the current key and generate a new one with the same name and scopes. You will need to update any applications using this key.'}
            </p>
            <div className="confirm-modal-actions">
              <button className="cancel-btn" onClick={() => setShowRotateConfirm(false)}>
                Cancel
              </button>
              <button className="confirm-rotate-btn" onClick={handleRotate} disabled={isRotating}>
                {isRotating ? <RefreshCw size={16} className="spinning" /> : <RotateCcw size={16} />}
                {isRotating
                  ? (hasPendingRotationRecovery ? 'Recovering...' : 'Rotating...')
                  : (hasPendingRotationRecovery ? 'Recover Key' : 'Rotate Key')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApiKeyDetailSidebar;
